import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Recruiter {
  id: string;
  name: string;
  email: string;
}

interface RecruiterSelectorProps {
  recruiters: Recruiter[];
  value?: string;
  onChange: (recruiterId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

export const RecruiterSelector: React.FC<RecruiterSelectorProps> = ({
  recruiters,
  value,
  onChange,
  placeholder = "Select a recruiter...",
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredRecruiters = recruiters.filter(recruiter =>
    recruiter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recruiter.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedRecruiter = recruiters.find(recruiter => recruiter.id === value);

  const handleSelect = (recruiter: Recruiter) => {
    onChange(recruiter.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "w-full px-4 py-2.5 rounded-xl border text-left",
          "bg-glass-surface/50 backdrop-blur-sm",
          "text-text-enterprise-primary placeholder-text-enterprise-tertiary",
          "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
          "transition-all duration-200",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "flex items-center justify-between",
          error ? "border-red-500/50" : "border-glass-border/40"
        )}
      >
        <span className={cn(
          "truncate",
          !selectedRecruiter && "text-text-enterprise-tertiary"
        )}>
          {selectedRecruiter 
            ? `${selectedRecruiter.name} (${selectedRecruiter.email})`
            : placeholder
          }
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {selectedRecruiter && !disabled && (
            <X
              className="w-4 h-4 text-text-enterprise-tertiary hover:text-text-enterprise-primary"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={cn(
            "w-4 h-4 text-text-enterprise-tertiary transition-transform",
            isOpen && "transform rotate-180"
          )} />
        </div>
      </button>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 glass-card rounded-xl border border-glass-border/40 shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-glass-border/40">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary w-4 h-4" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search recruiters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm glass-input rounded-lg focus:border-primary-500 focus:shadow-glow-primary focus:outline-none bg-glass-surface/50 border-glass-border/50"
              />
            </div>
          </div>

          {/* Recruiter List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredRecruiters.length === 0 ? (
              <div className="p-6 text-center text-text-enterprise-tertiary">
                <p className="text-sm">
                  {searchTerm ? 'No recruiters found matching your search.' : 'No recruiters available.'}
                </p>
              </div>
            ) : (
              <div className="py-1">
                {filteredRecruiters.map((recruiter) => (
                  <button
                    key={recruiter.id}
                    type="button"
                    onClick={() => handleSelect(recruiter)}
                    className={cn(
                      "w-full px-4 py-2.5 text-left text-sm transition-colors",
                      "hover:bg-glass-surface/50",
                      "flex items-center justify-between",
                      selectedRecruiter?.id === recruiter.id && "bg-primary-500/10"
                    )}
                  >
                    <div>
                      <div className="text-text-enterprise-primary font-medium">
                        {recruiter.name}
                      </div>
                      <div className="text-xs text-text-enterprise-tertiary">
                        {recruiter.email}
                      </div>
                    </div>
                    {selectedRecruiter?.id === recruiter.id && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

